/**
 * Weather look profiles + precipitation. Owned by `look`.
 *
 * `gameplay` owns which weather is ACTIVE and announces it with `world:weatherChanged`.
 * This file owns what each one LOOKS like, and cross-fades every visual parameter between
 * two profiles over the announced transition.
 *
 * The three weathers, from the reference:
 *   thaw       — bright spring afternoon. Real blue sky, hard directional shadows, wet
 *                sheen left on the road from melt, puddles. This is the Crazy Taxi frame.
 *   winter     — whiteout overcast. Sky and ground almost merge (sampled #F5F6F7 vs
 *                #E0E4E6). Desaturated but NOT muddy: max chroma across a real winter
 *                frame is ~0.11, and the fix for "flat" is VALUE separation, not saturation.
 *   nightrain  — sodium. Montreal at night is not blue-black, it is amber: every photo in
 *                reference/montreal at night is lit by high-pressure sodium bouncing off
 *                low cloud, with the wet road throwing long amber streaks back at you.
 *
 * Precipitation is drawn as two THREE.Points systems whose positions are a pure function of
 * (ctx.time.sim, camera pose, per-particle seed) — so a frozen shot is bit-identical (§7.3).
 * Screen-aligned point sprites are exactly right for rain: a vertical streak in the texture
 * stays vertical on screen, which is what rain actually does.
 */

import * as THREE from 'three';
import { LOOK_TUNING as L } from './tuning.js';
import { MAT_TUNING as MT } from '../materials/tuning.js';
import { SKY, LIGHT, GRADE } from '../materials/palette.js';

const WEATHERS = ['thaw', 'winter', 'nightrain'];

/**
 * Hex → Vector3 of RAW sRGB components, 0..1. NOT a THREE.Color: the grade's black floor is
 * applied AFTER the sRGB encode, so it must carry display values. `new THREE.Color(hex)`
 * would silently hand the shader linear ones and the floor would land 3x too low.
 */
function srgbVec(hex) {
  return new THREE.Vector3(((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255);
}

/** Build the full resolved parameter set for one weather id. */
function profileFor(w) {
  const sky = SKY[w];
  return {
    id: w,
    // sky
    zenith: new THREE.Color(sky.zenith),
    mid: new THREE.Color(sky.mid),
    horizon: new THREE.Color(sky.horizon),
    haze: new THREE.Color(sky.haze),
    ground: new THREE.Color(sky.ground),
    cloud: new THREE.Color(sky.cloud),
    cloudShade: new THREE.Color(sky.cloudShade),
    skySun: new THREE.Color(sky.sun),
    gradientPower: L.sky.gradientPower[w],
    horizonSoft: L.sky.horizonSoft[w],
    skyGain: L.sky.gain[w],
    sunSize: L.sky.sunSize[w],
    sunGlowPower: L.sky.sunGlowPower[w],
    sunGlowStrength: L.sky.sunGlowStrength[w],
    cloudAmount: L.sky.cloudAmount[w],
    cloudSharp: L.sky.cloudSharpness[w],
    cloudScale: L.sky.cloudScale[w],
    cloudOpacity: L.sky.cloudOpacity[w],

    // lights
    sunColor: new THREE.Color(LIGHT.sun[w]),
    sunElevation: L.light.sun[w].elevation,
    sunAzimuth: L.light.sun[w].azimuth,
    sunIntensity: L.light.sun[w].intensity,
    hemiSky: new THREE.Color(LIGHT.hemiSky[w]),
    hemiGround: new THREE.Color(LIGHT.hemiGround[w]),
    hemiIntensity: L.light.hemi[w],
    fillColor: new THREE.Color(LIGHT.fill[w]),
    fillIntensity: L.light.fill[w],
    envIntensity: L.light.envIntensity[w],

    // fog
    fogNear: L.fog.near[w],
    fogFar: L.fog.far[w],
    aerialNear: L.fog.aerialNear[w],
    aerialFar: L.fog.aerialFar[w],
    aerialLevel: L.fog.aerialLevel[w],

    // materials
    wetness: MT.weatherMat.wetness[w],
    snowCover: MT.weatherMat.snowCover[w],
    night: MT.weatherMat.night[w],

    // post
    bloomThreshold: L.post.bloomThreshold[w],
    bloomStrength: L.post.bloomStrength[w],
    saturation: L.post.saturation[w],
    contrast: L.post.contrast[w],
    shadowFloor: srgbVec(L.post.shadowFloor[w]),
    contrastPivot: L.post.contrastPivot[w],
    tint: new THREE.Vector3(L.post.tintR[w], L.post.tintG[w], L.post.tintB[w]),
    vignette: L.post.vignetteStrength[w],
    wetStreak: L.post.wetStreak[w],
    ao: L.post.ao.strength[w],

    // precipitation
    rain: w === 'nightrain' ? 1 : 0,
    snow: w === 'winter' ? 1 : 0,
  };
}

export const WEATHER_PROFILES = Object.freeze(
  WEATHERS.reduce((acc, w) => { acc[w] = profileFor(w); return acc; }, {})
);

/** An empty profile object we can blend into without allocating per frame. */
export function makeBlendTarget() {
  const p = profileFor('thaw');
  p.id = 'thaw';
  return p;
}

const _keysColor = [
  'zenith', 'mid', 'horizon', 'haze', 'ground', 'cloud', 'cloudShade', 'skySun',
  'sunColor', 'hemiSky', 'hemiGround', 'fillColor',
];
const _keysNumber = [
  'gradientPower', 'horizonSoft', 'skyGain', 'sunSize', 'sunGlowPower', 'sunGlowStrength',
  'cloudAmount', 'cloudSharp', 'cloudScale', 'cloudOpacity',
  'sunElevation', 'sunAzimuth', 'sunIntensity', 'hemiIntensity', 'fillIntensity', 'envIntensity',
  'fogNear', 'fogFar', 'aerialNear', 'aerialFar', 'aerialLevel', 'wetness', 'snowCover', 'night',
  'bloomThreshold', 'bloomStrength', 'saturation', 'contrast', 'contrastPivot', 'vignette',
  'wetStreak', 'ao',
  'rain', 'snow',
];

/**
 * Blend two profiles into `out`. Allocation-free.
 * Azimuth is blended the short way round so a thaw→nightrain swing does not spin the sun
 * through the whole sky.
 */
export function blendProfiles(a, b, t, out) {
  for (let i = 0; i < _keysColor.length; i++) {
    const k = _keysColor[i];
    out[k].copy(a[k]).lerp(b[k], t);
  }
  for (let i = 0; i < _keysNumber.length; i++) {
    const k = _keysNumber[i];
    if (k === 'sunAzimuth') continue;
    out[k] = a[k] + (b[k] - a[k]) * t;
  }
  let d = ((b.sunAzimuth - a.sunAzimuth) % 360 + 540) % 360 - 180;
  out.sunAzimuth = a.sunAzimuth + d * t;
  out.tint.copy(a.tint).lerp(b.tint, t);
  out.shadowFloor.copy(a.shadowFloor).lerp(b.shadowFloor, t);
  out.id = t < 0.5 ? a.id : b.id;
  return out;
}

/* ══════════════════════════════════════════════════════════════════════════════════════
   Precipitation
   ══════════════════════════════════════════════════════════════════════════════════════ */

/**
 * Rain and snow as camera-following THREE.Points clouds.
 *
 * Two draw calls total, zero per-frame allocation. Every particle's position is
 *     pos = wrap(seedOffset + fall(simTime), box) + cameraAnchor
 * which is a pure function of sim time and the camera pose — no integration, no state, so
 * `freezeClocks()` needs to do nothing at all and repeated captures are identical.
 *
 * If `fx` later wants to own precipitation, call `look.setPrecipitation(false)` and this
 * whole thing removes itself from the scene.
 */
export class Precipitation {
  /**
   * @param {import('../core/rng.js').Rng} rng   a fork, e.g. ctx.rng.fork('look.weather')
   * @param {object} materials  ctx.materials
   * @param {number} scale      config.quality.particleScale
   */
  constructor(rng, materials, scale = 1) {
    const W = L.weather;
    this.rainCount = Math.max(80, Math.round(W.rainCount * scale));
    this.snowCount = Math.max(60, Math.round(W.snowCount * scale));

    this.group = new THREE.Group();
    this.group.name = 'precipitation';
    this.group.frustumCulled = false;
    this.group.matrixAutoUpdate = false;

    this.rain = this._build(this.rainCount, rng, materials.get('weather.rain'), 'rain');
    this.snow = this._build(this.snowCount, rng, materials.get('weather.snow'), 'snow');
    this.group.add(this.rain.points, this.snow.points);

    this.rain.points.material.size = W.rainSize;
    this.snow.points.material.size = W.snowSize;

    this._anchor = new THREE.Vector3();
    this._fwd = new THREE.Vector3();
    this.enabled = true;
    this.rainAmount = 0;
    this.snowAmount = 0;
  }

  _build(count, rng, material, name) {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    // Per-particle constants: a stable position inside the unit box plus a speed jitter.
    const seed = new Float32Array(count * 4);
    for (let i = 0; i < count; i++) {
      seed[i * 4 + 0] = rng.next();          // u  (x within box)
      seed[i * 4 + 1] = rng.next();          // v  (y phase)
      seed[i * 4 + 2] = rng.next();          // w  (z within box)
      seed[i * 4 + 3] = rng.range(0.72, 1.35); // speed / size jitter
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.boundingSphere = new THREE.Sphere(new THREE.Vector3(), L.weather.boxWidth);
    const points = new THREE.Points(geo, material);
    points.name = 'precip.' + name;
    points.frustumCulled = false;
    points.matrixAutoUpdate = false;
    points.visible = false;
    points.renderOrder = 900;
    return { points, geo, pos, seed, count };
  }

  /**
   * @param {number} simTime   ctx.time.sim — the ONLY clock this reads
   * @param {THREE.Camera} camera
   * @param {number} rainAmount 0..1
   * @param {number} snowAmount 0..1
   */
  update(simTime, camera, rainAmount, snowAmount) {
    const W = L.weather;
    this.rainAmount = rainAmount;
    this.snowAmount = snowAmount;

    // Anchor the volume slightly ahead of the camera so the particles are where you look.
    camera.getWorldDirection(this._fwd);
    this._anchor.copy(camera.position).addScaledVector(this._fwd, W.boxForward);
    // Snap the anchor to a 1 m grid: without this, sub-metre camera jitter shifts every
    // particle's wrap phase and the rain visibly crawls sideways.
    this._anchor.x = Math.round(this._anchor.x);
    this._anchor.y = Math.round(camera.position.y);
    this._anchor.z = Math.round(this._anchor.z);

    const showRain = this.enabled && rainAmount > 0.02;
    const showSnow = this.enabled && snowAmount > 0.02;
    this.rain.points.visible = showRain;
    this.snow.points.visible = showSnow;

    if (showRain) {
      this._fill(this.rain, simTime, W.rainFallSpeed, rainAmount, false);
      this.rain.points.material.opacity = 0.42 * Math.pow(rainAmount, L.weather.fadeCurve);
    }
    if (showSnow) {
      this._fill(this.snow, simTime, W.snowFallSpeed, snowAmount, true);
      this.snow.points.material.opacity = 0.92 * Math.pow(snowAmount, L.weather.fadeCurve);
    }
  }

  _fill(sys, simTime, fallSpeed, amount, drift) {
    const W = L.weather;
    const { pos, seed, count } = sys;
    const hw = W.boxWidth, hh = W.boxHeight, hd = W.boxDepth;
    const ax = this._anchor.x, ay = this._anchor.y, az = this._anchor.z;
    // Fewer particles when the weather is fading in — cheaper than fading alpha alone.
    const live = Math.max(1, Math.round(count * Math.min(1, amount * 1.15)));

    for (let i = 0; i < count; i++) {
      const o = i * 3;
      if (i >= live) { pos[o + 1] = -9999; continue; }
      const s = i * 4;
      const u = seed[s], v = seed[s + 1], w = seed[s + 2], jit = seed[s + 3];

      // Fall: phase wraps through the box height. `v` staggers the start.
      let y = (v + 1.0 - ((simTime * fallSpeed * jit) / hh) % 1.0) % 1.0;
      let x = u;
      let z = w;

      if (drift) {
        // Snow wanders. Two out-of-phase sines keep it cheap and deterministic.
        const ph = (u + w) * 12.9898 + simTime * W.snowDriftFreq;
        x += Math.sin(ph) * (W.snowDriftAmp / hw) * 0.5;
        z += Math.cos(ph * 0.77 + 1.7) * (W.snowDriftAmp / hd) * 0.5;
        x -= Math.floor(x); z -= Math.floor(z);
      } else {
        // Rain slants. Slant is a function of fall phase so streaks stay parallel.
        const drop = (1.0 - y) * hh;
        x += (drop * W.rainSlantX / fallSpeed) / hw;
        z += (drop * W.rainSlantZ / fallSpeed) / hd;
        x -= Math.floor(x); z -= Math.floor(z);
      }

      pos[o] = ax + (x - 0.5) * hw;
      pos[o + 1] = ay + (y - 0.35) * hh;
      pos[o + 2] = az + (z - 0.5) * hd;
    }
    sys.geo.attributes.position.needsUpdate = true;
  }

  setEnabled(v) {
    this.enabled = !!v;
    if (!this.enabled) { this.rain.points.visible = false; this.snow.points.visible = false; }
  }

  /** Cheap checksum of a position buffer — the first thing to check if a shot self-diffs. */
  _sum(sys) {
    let h = 2166136261 >>> 0;
    const p = sys.pos;
    for (let i = 0; i < p.length; i += 7) {
      h ^= (p[i] * 1024) | 0;
      h = Math.imul(h, 16777619) >>> 0;
    }
    return h >>> 0;
  }

  stats() {
    return {
      rain: this.rain.points.visible ? Math.round(this.rainCount * Math.min(1, this.rainAmount * 1.15)) : 0,
      snow: this.snow.points.visible ? Math.round(this.snowCount * Math.min(1, this.snowAmount * 1.15)) : 0,
      rainSum: this._sum(this.rain),
      snowSum: this._sum(this.snow),
      anchor: [this._anchor.x, this._anchor.y, this._anchor.z],
    };
  }

  dispose() {
    this.rain.geo.dispose();
    this.snow.geo.dispose();
    this.group.clear();
  }
}

export { GRADE };
